import { useState } from "react";
import SectionHeading from "@/components/base/SectionHeading";
import { actors } from "@/mocks/actors";

type Actor = (typeof actors)[number];

function ActorCard({ actor }: { actor: Actor }) {
  const [open, setOpen] = useState(false);

  return (
    <article className="reveal group rounded-2xl border border-background-200 bg-background-50 overflow-hidden flex flex-col">
      <div className="relative">
        <div className="w-full h-64 overflow-hidden">
          <img
            src={actor.avatar}
            alt={`演员 ${actor.name} 的人物宣传照`}
            title={`${actor.name} 主演 代表作 演员档案`}
            className="w-full h-full object-cover object-top group-hover:scale-[1.04] transition-transform duration-500"
          />
        </div>
        <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-accent-500 text-background-50 text-[11px] font-label font-medium">
          {actor.roleTag}
        </span>
      </div>

      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-baseline gap-2">
          <h3 className="font-heading font-bold text-lg text-foreground-950">{actor.name}</h3>
          <span className="text-xs text-foreground-400 font-label">{actor.roman}</span>
        </div>

        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {[actor.age, actor.agency, `${actor.debut}出道`].map((tag) => (
            <span
              key={tag}
              className="px-2 py-0.5 rounded-md bg-secondary-50 border border-secondary-100 text-[11px] text-secondary-800 font-label"
            >
              {tag}
            </span>
          ))}
        </div>

        <p className="mt-3 text-sm leading-relaxed text-foreground-600 flex-1">{actor.bio}</p>

        <p className="mt-3 text-xs text-foreground-500">
          <span className="font-label text-foreground-700">代表作：</span>
          {actor.works}
        </p>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="mt-3 self-start inline-flex items-center gap-1.5 text-xs font-label font-medium text-primary-600 hover:text-primary-700 cursor-pointer"
        >
          {open ? "收起戏外小料" : "看戏外小料"}
          <i className={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
        </button>

        {open && (
          <ul className="mt-3 pt-3 border-t border-background-200 space-y-2">
            {actor.facts.map((fact) => (
              <li key={fact} className="flex items-start gap-2 text-xs text-foreground-600">
                <i className="ri-sparkling-line text-accent-500 mt-0.5"></i>
                {fact}
              </li>
            ))}
          </ul>
        )}
      </div>
    </article>
  );
}

export default function ActorSection() {
  return (
    <section id="actors" className="relative w-full py-14 md:py-20 bg-background-50">
      <div className="w-full px-4 md:px-8">
        <SectionHeading
          eyebrow="主演阵容档案"
          title="从角色往下，认识演员本人"
          desc="把高频出现的几位主演集中建档：出道年份、所属公司、代表作与戏外小料都在这里。想顺着演员表继续了解时，这一区就是你的下一站。"
          accent="accent"
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {actors.map((actor) => (
            <ActorCard key={actor.id} actor={actor} />
          ))}
        </div>
      </div>
    </section>
  );
}